import {
  DomainError,
  materialClassOf,
  type ShipTime,
  shipLocalDate,
  validateWorkRecord,
  workRecordOverdue,
} from '@felix/domain';
import { z } from 'zod';
import { zoneOffsetMinutes } from '../personnel/rest-hours.js';
import type { MeasurementEntry } from './condition-types.js';
import { shipLocalToUtc, utcToShipLocal } from './counter-form.js';
import type {
  HistoryEntry,
  JobView,
  SparePartOption,
  WorkOrderView,
  WorkRecordForm,
} from './types.js';

/** Gemi stoğunun kalemi (stok listesinden); seçici bunlardan kurulur. */
export interface SparePartStockItem {
  materialId: string;
  name: string;
  specification?: string | null;
  unit: string;
  materialType?: string;
  companyCode?: string | null;
  impaCode?: string | null;
  onHand?: number | null;
}

/**
 * Yedek parça seçicisinin seçenekleri (ADR 0122 §4): kumanya düşer (gemi sunucusu da süzer;
 * eski sürümlü sunucuya karşı burada da), adla sıralı. Etiket ad, özellik ve kod taşır; arama
 * etikette yapıldığı için kodla da bulunur.
 */
export function sparePartOptions(items: readonly SparePartStockItem[]): SparePartOption[] {
  return items
    .filter((item) => materialClassOf(item.materialType ?? '') === 'stores')
    .map((item) => {
      const code = item.companyCode || item.impaCode || null;
      const spec = item.specification ? ` · ${item.specification}` : '';
      return {
        materialId: item.materialId,
        name: item.name,
        label: `${item.name}${spec}${code ? ` — ${code}` : ''}`,
        code,
        unit: item.unit,
        onHand: typeof item.onHand === 'number' ? item.onHand : null,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label, 'en'));
}

/**
 * Complete & Report formu (React Hook Form + Zod; plan M7 Task 14b; PMS belgesi §13): yapılış anı
 * gemi saatiyle, sayaç değeri, uygulayıcılar, yapılan iş, kontrol listesi, sonuç, onarım sonrası
 * durum, kullanılan parçalar ve not; ayrıca işin güvenlik maddeleri. Kurallar domain'in
 * `validateWorkRecord`'undan: sunucu aynı kuralı yeniden denetler [NR496 4.5.3f].
 */

export type ChecklistAnswerValue = 'pass' | 'fail' | 'na' | '';

export interface ReportFormValues {
  /** Gemi saatiyle `YYYY-MM-DDTHH:mm`. */
  doneAt: string;
  meterHours: number | '';
  performers: { userId: string | null; name: string }[];
  workPerformed: string;
  checklist: { answer: ChecklistAnswerValue; value: number | ''; remark: string }[];
  result: 'pass' | 'fail' | '';
  /** `''`: seçilmedi. */
  conditionAfter: string;
  /** Bakım öncesi durum (ADR 0132 §2; DNV MAD `conditionbefore`): isteğe bağlı, `''` girilmedi. */
  conditionBefore: string;
  /** Gerçek adam-saat (ADR 0132 §2; DNV MAD `workhours`): isteğe bağlı. */
  workHours: number | '';
  spareParts: { materialId: string; quantity: number | '' }[];
  remarks: string;
  /** Gecikme gerekçesi [NR496 4.5.4b] (ADR 0029 Ek M7f F1): yalnız gecikmiş işte görünür. */
  overdueReason: string;
  /** CM raporu servis sağlayıcıdan bekleniyor [NR496 4.5.4e] (F4). */
  cmReportPending: boolean;
  /** İşin güvenlik maddeleri (PMS belgesi §12). */
  safety: boolean[];
  /** Measurements adımı (K19): nokta kimliği → değer; `''` girilmedi. */
  measurements: Record<string, number | ''>;
}

type JobShape = Pick<JobView, 'checklist' | 'safetyItems' | 'interval'> | null;

export function emptyReportForm(input: {
  job: JobShape;
  now: string;
  timeZone: ShipTime;
  me: { id: string; name: string } | null;
  safety?: readonly boolean[];
}): ReportFormValues {
  return {
    doneAt: utcToShipLocal(input.now, input.timeZone),
    meterHours: '',
    performers: input.me
      ? [{ userId: input.me.id, name: input.me.name }]
      : [{ userId: null, name: '' }],
    workPerformed: '',
    checklist: (input.job?.checklist ?? []).map(() => ({ answer: '', value: '', remark: '' })),
    result: '',
    conditionAfter: '',
    conditionBefore: '',
    workHours: '',
    spareParts: [],
    remarks: '',
    overdueReason: '',
    cmReportPending: false,
    safety: (input.job?.safetyItems ?? []).map((_, i) => input.safety?.[i] === true),
    measurements: {},
  };
}

/** Raporlanmış kayıt forma (düzeltmede yeni kayıt önceden dolar; K14). */
export function reportFormOf(
  entry: HistoryEntry,
  job: JobShape,
  timeZone: ShipTime,
): ReportFormValues {
  const answers = new Map((entry.checklistResult ?? []).map((c) => [c.index, c]));
  return {
    doneAt: utcToShipLocal(entry.doneAt, timeZone),
    meterHours: entry.meterHours ?? '',
    performers: (entry.performedBy ?? []).map((p) => ({ userId: p.userId, name: p.name })),
    workPerformed: entry.workPerformed,
    checklist: (job?.checklist ?? []).map((_, index) => {
      const answer = answers.get(index);
      return {
        answer: (answer?.answer ?? '') as ChecklistAnswerValue,
        value: answer?.value ?? '',
        remark: answer?.remark ?? '',
      };
    }),
    result: entry.result === 'pass' || entry.result === 'fail' ? entry.result : '',
    conditionAfter: entry.conditionAfter ?? '',
    conditionBefore: entry.conditionBefore ?? '',
    workHours: entry.workHours ?? '',
    spareParts: (entry.sparePartsUsed ?? []).map((p) => ({ ...p })),
    remarks: entry.remarks ?? '',
    overdueReason: entry.overdueReason ?? '',
    cmReportPending: entry.cmReportPending === true,
    safety: (job?.safetyItems ?? []).map(() => true),
    measurements: {},
  };
}

const blank = (value: string) => (value.trim() === '' ? null : value.trim());

/** Formdan sunucunun beklediği kayıt; an UTC ve gemi saati farkıyla (CLAUDE.md "Zaman"). */
export function toWorkRecordForm(values: ReportFormValues, timeZone: ShipTime): WorkRecordForm {
  const doneAt = shipLocalToUtc(values.doneAt, timeZone) ?? values.doneAt;
  return {
    doneAt,
    tzOffsetMin: zoneOffsetMinutes(timeZone, Date.parse(doneAt)),
    meterHours: values.meterHours === '' ? null : values.meterHours,
    performedBy: values.performers
      .filter((p) => p.name.trim() !== '')
      .map((p) => ({ userId: p.userId, name: p.name.trim() })),
    workPerformed: values.workPerformed.trim(),
    checklistResult: values.checklist.map((item, index) => ({
      index,
      answer: (item.answer === '' ? 'na' : item.answer) as 'pass' | 'fail' | 'na',
      value: item.value === '' ? null : item.value,
      remark: blank(item.remark),
    })),
    result: (values.result === '' ? 'pass' : values.result) as 'pass' | 'fail',
    conditionAfter: values.conditionAfter === '' ? null : values.conditionAfter,
    sparePartsUsed: values.spareParts
      .filter((p) => p.materialId !== '' && p.quantity !== '')
      .map((p) => ({ materialId: p.materialId, quantity: Number(p.quantity) })),
    remarks: blank(values.remarks),
    // Yalnız doluyken (F1, F4): kanonik içerik eski kayıtlarla aynı biçimde kalır
    ...(blank(values.overdueReason ?? '') ? { overdueReason: blank(values.overdueReason) } : {}),
    ...(values.cmReportPending === true ? { cmReportPending: true } : {}),
    // Yalnız doluyken (ADR 0132 §2): boş form eski kayıtlarla aynı içerik
    ...(values.conditionBefore ? { conditionBefore: values.conditionBefore } : {}),
    ...(typeof values.workHours === 'number' ? { workHours: values.workHours } : {}),
  };
}

/**
 * Formda gecikme gerekçesi istenir mi [NR496 4.5.4b] (ADR 0029 Ek M7f F1, F3): plan motoru işi
 * gecikmiş sayıyorsa ya da bugün (raporlama günü) vadeden sonraysa ya da girilen sayaç değeri
 * vade saatini aştıysa. Plansız iş emrinde vade hedef tarihtir. Sunucu aynı kuralı kaydın
 * kendi alanlarıyla yeniden denetler.
 */
export function reportOverdue(
  wo: Pick<WorkOrderView, 'jobId' | 'targetOn' | 'due'>,
  today: string,
  meterHours: number | '',
): boolean {
  if (wo.jobId !== null && wo.due.status === 'overdue') return true;
  return workRecordOverdue({
    reportedOn: today,
    runningHours: meterHours === '' ? null : meterHours,
    dueOn: wo.jobId !== null ? wo.due.dueOn : (wo.due.targetOn ?? wo.targetOn),
    dueHours: wo.jobId !== null ? wo.due.dueHours : null,
  });
}

/**
 * Measurements adımının girilen değerleri (K19): raporla birlikte yazılır; ölçüm anı işin yapılış
 * anıdır (gemi saatiyle girilen, UTC'ye çevrilen).
 */
export function toMeasurementEntries(
  values: Pick<ReportFormValues, 'doneAt' | 'measurements'>,
  timeZone: ShipTime,
): MeasurementEntry[] {
  const measuredAt = shipLocalToUtc(values.doneAt, timeZone) ?? undefined;
  return Object.entries(values.measurements ?? {})
    .filter((entry): entry is [string, number] => typeof entry[1] === 'number')
    .map(([pointId, value]) => ({ pointId, value, ...(measuredAt ? { measuredAt } : {}) }));
}

/** Domain hata kodu → formun alanı. */
function fieldOf(error: DomainError): (string | number)[] {
  switch (error.code) {
    case 'INVALID_DATE':
    case 'FUTURE_DATE':
    case 'DONE_ON_MISMATCH':
      return ['doneAt'];
    case 'RUNNING_HOURS_REQUIRED':
    case 'INVALID_HOURS':
    case 'NEGATIVE_VALUE':
      return ['meterHours'];
    case 'PERFORMER_REQUIRED':
      return ['performers'];
    case 'WORK_DESCRIPTION_REQUIRED':
      return ['workPerformed'];
    case 'CHECKLIST_REMARK_REQUIRED':
      return ['checklist', Number(error.message), 'remark'];
    case 'CHECKLIST_VALUE_REQUIRED':
      return ['checklist', Number(error.message), 'value'];
    case 'RESULT_REQUIRED':
    case 'RESULT_INCONSISTENT':
      return ['result'];
    case 'CONDITION_AFTER_REQUIRED':
    case 'INVALID_CONDITION':
      return ['conditionAfter'];
    case 'OVERDUE_REASON_REQUIRED':
      return ['overdueReason'];
    case 'CM_REPORT_REQUIRED':
      return ['cmReportPending'];
    case 'INVALID_WORK_HOURS':
      return ['workHours'];
    case 'INVALID_SPARE_PARTS':
      return ['spareParts'];
    default:
      return ['checklist'];
  }
}

const numberField = z.union([z.number(), z.literal('')]);

/**
 * Formun şeması: güvenlik maddeleri işaretli (`SAFETY_CHECK_INCOMPLETE`); boş cevap ve boş sonuç
 * eksik sayılır; diğer kurallar `validateWorkRecord` ile. `serverNow`: gemi sunucusunun saati
 * (gelecek tarih yok; mimari belge §6.11).
 */
export function reportFormSchema(options: {
  job: JobShape;
  kind: string;
  timeZone: ShipTime;
  serverNow: string;
  /** Tetikleyici noktalar: raporda değer zorunlu (`PARAMETER_REQUIRED`; ADR 0029 C3). */
  requiredPoints?: readonly string[];
  /**
   * İş gecikmiş raporlanıyor (`reportOverdue`; ADR 0029 Ek M7f F1). Fonksiyon formun değerleriyle
   * çağrılır: girilen sayaç değeri vade saatini aşabilir.
   */
  overdue?: boolean | ((values: ReportFormValues) => boolean);
  /** Durum bazlı iş: rapor eki ya da beyan (F4). */
  cmReport?: { required: boolean; attached: boolean };
}) {
  return z
    .object({
      doneAt: z.string(),
      meterHours: numberField,
      performers: z.array(z.object({ userId: z.string().nullable(), name: z.string() })),
      workPerformed: z.string(),
      checklist: z.array(
        z.object({
          answer: z.enum(['pass', 'fail', 'na', '']),
          value: numberField,
          remark: z.string(),
        }),
      ),
      result: z.enum(['pass', 'fail', '']),
      conditionAfter: z.string(),
      conditionBefore: z.string(),
      workHours: numberField,
      spareParts: z.array(z.object({ materialId: z.string(), quantity: numberField })),
      remarks: z.string(),
      overdueReason: z.string().max(4000),
      cmReportPending: z.boolean(),
      safety: z.array(z.boolean()),
      measurements: z.record(z.string(), numberField),
    })
    .superRefine((values, ctx) => {
      if (values.safety.some((checked) => !checked)) {
        ctx.addIssue({ code: 'custom', message: 'SAFETY_CHECK_INCOMPLETE', path: ['safety'] });
      }
      const missing = (options.requiredPoints ?? []).filter(
        (id) => typeof values.measurements[id] !== 'number',
      );
      if (missing.length > 0) {
        ctx.addIssue({ code: 'custom', message: 'PARAMETER_REQUIRED', path: ['measurements'] });
      }
      const record = toWorkRecordForm(values as ReportFormValues, options.timeZone);
      try {
        if (values.checklist.some((c) => c.answer === '')) {
          // Önce sırayla: saat, uygulayıcı ve iş kuralları; sonra eksik cevap
          validateWorkRecord(
            {
              ...record,
              conditionBefore: (record.conditionBefore ?? null) as 'operational' | null,
              kind: options.kind as 'planned',
              doneOn: shipLocalDate(record.doneAt, record.tzOffsetMin ?? 0),
              tzOffsetMin: record.tzOffsetMin ?? 0,
              checklistResult: [],
              conditionAfter: (record.conditionAfter ?? 'operational') as 'operational',
            },
            {
              serverNow: options.serverNow,
              hoursBased: (options.job?.interval.runningHours ?? null) !== null,
              checklist: [],
            },
          );
          throw new DomainError('CHECKLIST_INCOMPLETE');
        }
        if (values.result === '') {
          validateWorkRecord(
            {
              ...record,
              conditionBefore: (record.conditionBefore ?? null) as 'operational' | null,
              kind: options.kind as 'planned',
              doneOn: shipLocalDate(record.doneAt, record.tzOffsetMin ?? 0),
              tzOffsetMin: record.tzOffsetMin ?? 0,
              result: 'fail',
              conditionAfter: 'operational',
            },
            {
              serverNow: options.serverNow,
              hoursBased: (options.job?.interval.runningHours ?? null) !== null,
              checklist: (options.job?.checklist ?? []).map((c) => ({
                text: c.text,
                kind: c.kind,
              })),
            },
          );
          throw new DomainError('RESULT_REQUIRED');
        }
        validateWorkRecord(
          {
            ...record,
            conditionBefore: (record.conditionBefore ?? null) as 'operational' | null,
            kind: options.kind as 'planned',
            doneOn: shipLocalDate(record.doneAt, record.tzOffsetMin ?? 0),
            tzOffsetMin: record.tzOffsetMin ?? 0,
            conditionAfter: record.conditionAfter as null,
          },
          {
            serverNow: options.serverNow,
            hoursBased: (options.job?.interval.runningHours ?? null) !== null,
            checklist: (options.job?.checklist ?? []).map((c) => ({ text: c.text, kind: c.kind })),
            ...((
              typeof options.overdue === 'function'
                ? options.overdue(values as ReportFormValues)
                : options.overdue === true
            )
              ? { overdue: true }
              : {}),
            ...(options.cmReport ? { cmReport: options.cmReport } : {}),
          },
        );
      } catch (error) {
        if (!(error instanceof DomainError)) throw error;
        ctx.addIssue({ code: 'custom', message: error.code, path: fieldOf(error) });
      }
    });
}
